
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useI18n } from '@/lib/i18n';

// UI_BUILD_SPEC §2.18. Active filters must be visible on the page, not hidden
// behind a drawer with a count — an agent who cannot see why a queue is short
// will assume the queue is empty.

interface FilterChipProps {
  field: string;
  value: string;
  onRemove: () => void;
}

export function FilterChip({ field, value, onRemove }: FilterChipProps) {
  const { t } = useI18n();
  return (
    <span className="inline-flex h-7 items-center gap-1 rounded-full border border-brand-100 bg-brand-50 pl-2.5 pr-1 text-sm text-brand-700">
      <span className="max-w-[22ch] truncate">
        <span className="text-brand-600/80">{field}:</span> {value}
      </span>
      <button
        type="button"
        onClick={onRemove}
        // Announced imperatively because removing the chip also removes this
        // button from the DOM — a live region on the chip itself would unmount
        // before a screen reader reached it.
        aria-label={t('ui.removeFilter', { field, value })}
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-brand-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <X className="h-3 w-3" aria-hidden="true" />
      </button>
    </span>
  );
}

interface FilterChipRowProps {
  chips: Array<{ key: string; field: string; value: string; onRemove: () => void }>;
  onClearAll: () => void;
  className?: string;
}

export function FilterChipRow({ chips, onClearAll, className }: FilterChipRowProps) {
  const { t } = useI18n();
  if (chips.length === 0) return null;
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {chips.map((chip) => (
        <FilterChip key={chip.key} field={chip.field} value={chip.value} onRemove={chip.onRemove} />
      ))}
      {chips.length > 1 && (
        <button
          type="button"
          onClick={onClearAll}
          className="rounded px-1 text-sm text-ink-subtle underline-offset-4 hover:text-brand-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          {t('tickets.filtersClearAll')}
        </button>
      )}
    </div>
  );
}
